'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Time to move from trigger across the gap to the fixed panel (ms). */
const CLOSE_DELAY_MS = 280;

/**
 * Shared registry of every mounted hover-menu's immediate-close callback, keyed by a unique id.
 * When one menu opens it forces any other open menu to begin its exit at once, so two desktop
 * dropdowns can never linger visible together while the pointer switches between triggers.
 */
const menuClosers = new Map<number, () => void>();
let nextMenuId = 1;

/**
 * Keeps mega-menus open while moving the pointer from the trigger to a fixed dropdown.
 * Pure CSS :hover fails because the pointer leaves the small wrapper before entering the panel.
 */
export function useHoverMenuOpen() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextMenuId++;

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  /** Immediately begin this menu's exit (cancels any pending delayed close). */
  const closeNow = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);

  // Register this menu's immediate-close callback while mounted so peers can dismiss it.
  useEffect(() => {
    const id = idRef.current;
    menuClosers.set(id, closeNow);
    return () => {
      menuClosers.delete(id);
    };
  }, [closeNow]);

  const openMenu = useCallback(() => {
    // Any other open menu must start exiting right away — no 280ms lingering overlap.
    menuClosers.forEach((close, id) => {
      if (id !== idRef.current) close();
    });
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  return { open, openMenu, scheduleClose };
}
